import React from 'react'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import {
  ButtonAction,
  CustomAction,
} from 'Components/SharedComponents/Surveys/types'
import { HandleAnyAction } from '../../types'
import { NeedsAssessmentWizardStep } from '../../constants'
import { sendStepTransitionEvent } from '../../utils'

type WhatIsYourRoleProps = {
  onAnyAction: HandleAnyAction
  onSetUserRole: (role: UserSurveyRole) => void
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole
}

export const WhatIsYourRole = ({
  onAnyAction,
  onSetUserRole,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: WhatIsYourRoleProps) => {
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const userActions: CustomAction[] = [
    {
      callback: () => onSetUserRole(UserSurveyRole.Executive),
      checkValue: userRole,
      label: 'Executive (CEO, Executive Director, VP, Founder, etc)',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.Executive,
    },
    {
      callback: () => onSetUserRole(UserSurveyRole.Director),
      checkValue: userRole,
      label: 'Director',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.Director,
    },
    {
      callback: () => onSetUserRole(UserSurveyRole.Manager),
      checkValue: userRole,
      label: 'Manager',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.Manager,
    },
    {
      callback: () =>
        onSetUserRole(UserSurveyRole.AssistantAssociateCoordinator),
      checkValue: userRole,
      label: 'Assistant / Associate / Coordinator',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.AssistantAssociateCoordinator,
    },
    {
      callback: () => onSetUserRole(UserSurveyRole.Volunteer),
      checkValue: userRole,
      label: 'Volunteer',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.Volunteer,
    },
    {
      callback: () => onSetUserRole(UserSurveyRole.BoardMember),
      checkValue: userRole,
      label: 'Board Member',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.BoardMember,
    },
    {
      callback: () => onSetUserRole(UserSurveyRole.TeamMember),
      checkValue: userRole,
      label: 'Other',
      type: ActionElementType.RADIO,
      value: UserSurveyRole.TeamMember,
    },
  ]

  const nextStep = NeedsAssessmentWizardStep.WHAT_IS_YOUR_PRIMARY_FUNCTION
  const nextActions: ButtonAction[] = [
    {
      disabled: !userRole,
      label: 'Next',
      path: nextStep,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: () => {
        sendStepTransitionEvent({
          currentStep: NeedsAssessmentWizardStep.WHAT_IS_YOUR_ROLE,
          nextStep,
          userId: userConfig.id,
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          funderOrgId: nonprofitOrgConfig.funderOrgId,
          userEmail: userConfig.email,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
  ]

  return (
    <StepContainer>
      <Title>What is your current job level within your organization?</Title>
      <Description>
        Every member of your organization contributes in a unique way!
        Organization Leaders can set priorities and goals for the entire
        organization to build capacity while Members can choose priorities to
        help us personalize your experience.
      </Description>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
